import type { Ref } from "react";

import TickerLookupField from "@/domains/watches/components/TickerLookupField";
import WatchSignalPicker from "@/domains/watches/components/WatchSignalPicker";
import type {
  WatchMarketSignalSelection,
  WatchMarketRecipeSelection,
  WatchMomentumDraft,
  WatchMovingAverageDraft,
} from "@/domains/watches/hooks/useWatchAuthoringState";
import type { TickerSuggestion } from "@/shared/lib/types";
import type {
  WatchSignalCatalog,
  WatchSignalDefinition,
} from "../../lib/watchSignalCatalog";
import type { WatchRecipeCatalog, WatchRecipeDefinition } from "../../lib/watchRecipeCatalog";
import { findWatchSignal } from "../../lib/watchSignalCatalog";
import { canonicalRatioToPercent, percentToCanonicalRatio } from "../../lib/watchRatioPresentation";
import {
  describeCompletedDailyVolatilityCondition,
  describeWatchSignalCondition,
  getReviewedSignalPresenter,
  isCompletedDailyVolatilityPresenter,
  isStandardizedOptionChainPresenter,
  type CompletedDailyVolatilityMeasurement,
} from "../../lib/watchSignalReleaseRegistry";
import wizardStyles from "../WatchCreateWizard/WatchCreateWizard.module.css";
import styles from "./WatchMarketConditionFields.module.css";

export type WatchMarketConditionMode = "subject" | "condition";
export type WatchMarketTargetOperator = "gte" | "lte";

interface WatchMarketConditionBaseProps {
  className?: string;
}

export interface WatchMarketSubjectFieldsProps extends WatchMarketConditionBaseProps {
  mode: "subject";
  signalCatalog: WatchSignalCatalog | null;
  selectedSignal: WatchMarketSignalSelection | null;
  recipeCatalog?: WatchRecipeCatalog | null;
  selectedRecipe?: WatchMarketRecipeSelection | null;
  ticker: string;
  confirmedTicker: string;
  errorMessageId: string;
  invalidSignal: boolean;
  invalidReference: boolean;
  invalidMovingAverageComparison: boolean;
  invalidTicker: boolean;
  onSelectSignal: (signal: WatchSignalDefinition) => void;
  onSelectRecipe?: (recipe: WatchRecipeDefinition | null) => void;
  onPercentageExpandedChange: (expanded: boolean) => void;
  onMovingAverageExpandedChange: (expanded: boolean) => void;
  movingAverageDraft: WatchMovingAverageDraft | null;
  onSelectMovingAverageComparison: (
    comparison: WatchMovingAverageDraft["comparison"],
  ) => void;
  onTickerChange: (value: string) => void;
  onTickerSelect: (suggestion: TickerSuggestion) => void;
  searchTickers?: (query: string) => Promise<TickerSuggestion[]>;
  percentageReferenceRef?: Ref<HTMLFieldSetElement>;
  movingAverageComparisonRef?: Ref<HTMLFieldSetElement>;
  momentumDraft: WatchMomentumDraft | null;
  onSelectMomentumIndicator: (indicator: WatchMomentumDraft["indicator"]) => void;
  invalidMomentumIndicator: boolean;
  onMomentumExpandedChange: (expanded: boolean) => void;
  momentumIndicatorRef?: Ref<HTMLFieldSetElement>;
  selectedVolatilityMeasurement: CompletedDailyVolatilityMeasurement | null;
  onVolatilityExpandedChange: (expanded: boolean) => void;
  invalidVolatilityMeasurement: boolean;
  volatilityMeasurementRef?: Ref<HTMLFieldSetElement>;
}

export interface WatchMarketTargetFieldsProps extends WatchMarketConditionBaseProps {
  mode: "condition";
  signalCatalog: WatchSignalCatalog | null;
  selectedSignal: WatchMarketSignalSelection | null;
  targetOperator: WatchMarketTargetOperator | null;
  targetThreshold: string;
  underlying: string;
  errorMessageId: string;
  invalidThreshold: boolean;
  invalidOperator: boolean;
  onTargetOperatorChange: (operator: WatchMarketTargetOperator) => void;
  onTargetThresholdChange: (threshold: string) => void;
  thresholdInputRef?: Ref<HTMLInputElement>;
  operatorGroupRef?: Ref<HTMLFieldSetElement>;
  /** Latest observed underlying price, shown as context beside the target input. */
  currentPrice?: number | null;
  /** Prior session's close, when the quote payload already carries it. Null hides the "Prev close" chip. */
  previousClose?: number | null;
  /** Sets the target price (and infers direction) from a preset chip tap. */
  onSelectPricePreset?: (price: number) => void;
  /** True while the direction radio reflects an inferred, still-overridable choice. */
  directionInferred?: boolean;
}

export type WatchMarketConditionFieldsProps =
  | WatchMarketSubjectFieldsProps
  | WatchMarketTargetFieldsProps;

/** Deterministic percentage offsets shown as target-price preset chips, anchored to the live quote. */
const PRICE_PRESET_OFFSETS = [-5, -2, -1, 1, 2, 5] as const;

/** Controlled exact-price authoring fields for the market Watch flow. */
export default function WatchMarketConditionFields(
  props: WatchMarketConditionFieldsProps,
) {
  if (props.mode === "subject") {
    const {
      signalCatalog,
      selectedSignal,
      recipeCatalog = null,
      selectedRecipe = null,
      ticker,
      confirmedTicker,
      errorMessageId,
      invalidSignal,
      invalidReference,
      invalidMovingAverageComparison,
      invalidTicker,
      onSelectSignal,
      onSelectRecipe,
      onPercentageExpandedChange,
      onMovingAverageExpandedChange,
      movingAverageDraft,
      onSelectMovingAverageComparison,
      onTickerChange,
      onTickerSelect,
      searchTickers,
      percentageReferenceRef,
      movingAverageComparisonRef,
      momentumDraft,
      onSelectMomentumIndicator,
      invalidMomentumIndicator,
      onMomentumExpandedChange,
      momentumIndicatorRef,
      selectedVolatilityMeasurement,
      onVolatilityExpandedChange,
      invalidVolatilityMeasurement,
      volatilityMeasurementRef,
      className,
    } = props;
    return (
      <div className={`${styles.root} ${className ?? ""}`}>
        <div className={wizardStyles.singleField}>
          <TickerLookupField
            value={ticker}
            confirmedValue={confirmedTicker}
            onValueChange={onTickerChange}
            onSelect={onTickerSelect}
            searchTickers={searchTickers}
            label="Ticker"
            ariaLabel="Underlying symbol"
            invalid={invalidTicker}
            errorMessageId={errorMessageId}
            autoFocus
          />
        </div>
        {signalCatalog ? (
          <WatchSignalPicker
            catalog={signalCatalog}
            selected={selectedSignal}
            onSelect={onSelectSignal}
            recipeCatalog={recipeCatalog}
            selectedRecipe={selectedRecipe}
            onSelectRecipe={onSelectRecipe}
            invalid={invalidSignal}
            invalidReference={invalidReference}
            errorMessageId={errorMessageId}
            onPercentageExpandedChange={onPercentageExpandedChange}
            percentageReferenceRef={percentageReferenceRef}
            selectedMovingAverageComparison={movingAverageDraft?.comparison ?? null}
            onSelectMovingAverageComparison={onSelectMovingAverageComparison}
            invalidMovingAverageComparison={invalidMovingAverageComparison}
            onMovingAverageExpandedChange={onMovingAverageExpandedChange}
            movingAverageComparisonRef={movingAverageComparisonRef}
            selectedMomentumIndicator={momentumDraft?.indicator ?? null}
            onSelectMomentumIndicator={onSelectMomentumIndicator}
            invalidMomentumIndicator={invalidMomentumIndicator}
            onMomentumExpandedChange={onMomentumExpandedChange}
            momentumIndicatorRef={momentumIndicatorRef}
            selectedVolatilityMeasurement={selectedVolatilityMeasurement}
            onVolatilityExpandedChange={onVolatilityExpandedChange}
            invalidVolatilityMeasurement={invalidVolatilityMeasurement}
            volatilityMeasurementRef={volatilityMeasurementRef}
          />
        ) : null}
      </div>
    );
  }

  const {
    targetOperator,
    targetThreshold,
    underlying,
    errorMessageId,
    invalidThreshold,
    invalidOperator,
    onTargetOperatorChange,
    onTargetThresholdChange,
    thresholdInputRef,
    operatorGroupRef,
    className,
    signalCatalog,
    selectedSignal,
    currentPrice = null,
    previousClose = null,
    onSelectPricePreset,
    directionInferred = false,
  } = props;
  const definition = selectedSignal
    ? findWatchSignal(
      signalCatalog,
      selectedSignal.signalId,
      selectedSignal.definitionVersion,
    )
    : null;
  const presenter = definition ? getReviewedSignalPresenter(definition) : null;
  const isPercentageMove = presenter?.kind === "PERCENTAGE_MOVE";

  if (isCompletedDailyVolatilityPresenter(presenter)) {
    const direction = targetOperator === "gte" ? "ABOVE" : targetOperator === "lte" ? "BELOW" : null;
    const displaysAsPercent = presenter.measurement === "BOLLINGER_BANDWIDTH_20_2"
      || presenter.measurement === "REALIZED_VOLATILITY_20D";
    const labels = presenter.measurement === "ATR14"
      ? { field: "Average true range", placeholder: "2.50", note: "Enter a dollar amount per share.", prefix: "$", suffix: "" }
      : presenter.measurement === "BOLLINGER_PERCENT_B_20_2"
        ? { field: "Bollinger %B level", placeholder: "0.5", note: "0 is the lower band, 0.5 the middle, and 1 the upper band; values can extend beyond the bands.", prefix: "", suffix: "" }
        : presenter.measurement === "BOLLINGER_BANDWIDTH_20_2"
          ? { field: "Bollinger band width", placeholder: "20", note: "Enter the band width as a percentage of the 20-day middle band. For example, 20 means 20%.", prefix: "", suffix: "%" }
          : { field: "20-day annualized realized volatility", placeholder: "25", note: "Enter annualized realized volatility as a percentage. For example, 25 means 25%.", prefix: "", suffix: "%" };
    return (
      <div className={`${styles.root} ${className ?? ""}`}>
        <p className={wizardStyles.help}>Set a completed-day boundary. Reaching the boundary counts; this is not a crossing alert.</p>
        <div className={wizardStyles.triggerRow}>
          <label>
            <span>{labels.field}</span>
            <span className={displaysAsPercent ? wizardStyles.percentageInput : undefined}>{labels.prefix}<input
              ref={thresholdInputRef}
              value={displaysAsPercent ? canonicalRatioToPercent(targetThreshold) : targetThreshold}
              onChange={(event) => onTargetThresholdChange(
                displaysAsPercent ? percentToCanonicalRatio(event.target.value) : event.target.value,
              )}
              inputMode="decimal"
              placeholder={labels.placeholder}
              aria-label={labels.field}
              aria-invalid={invalidThreshold || undefined}
              aria-describedby={invalidThreshold ? errorMessageId : undefined}
            />{labels.suffix ? <span aria-hidden="true">{labels.suffix}</span> : null}</span>
            <small>{labels.note}</small>
          </label>
          <fieldset
            ref={operatorGroupRef}
            aria-invalid={invalidOperator || undefined}
            aria-describedby={invalidOperator ? errorMessageId : undefined}
          >
            <legend>Alert when the measurement is</legend>
            <label><input type="radio" name="target-direction" checked={targetOperator === "gte"} onChange={() => onTargetOperatorChange("gte")} />At or above</label>
            <label><input type="radio" name="target-direction" checked={targetOperator === "lte"} onChange={() => onTargetOperatorChange("lte")} />At or below</label>
          </fieldset>
        </div>
        <p className={wizardStyles.rulePreview}>
          <strong>Alert rule</strong>
          {describeCompletedDailyVolatilityCondition({ ticker: underlying, measurement: presenter.measurement, direction, level: targetThreshold })}
        </p>
      </div>
    );
  }

  if (isPercentageMove) {
    return (
      <div className={`${styles.root} ${className ?? ""}`}>
        <p className={wizardStyles.help}>
          Choose a direction and a positive percentage. Reaching the boundary counts.
        </p>
        <p className={styles.referenceContext}>
          <strong>Compared with</strong>
          {presenter.referenceLabel}
        </p>
        <div className={styles.percentageFields}>
          <fieldset
            ref={operatorGroupRef}
            className={styles.directionChoices}
            aria-invalid={invalidOperator || undefined}
            aria-describedby={invalidOperator ? errorMessageId : undefined}
          >
            <legend>Direction</legend>
            <label>
              <input
                type="radio"
                name="target-direction"
                checked={targetOperator === "gte"}
                onChange={() => onTargetOperatorChange("gte")}
              />
              Up
            </label>
            <label>
              <input
                type="radio"
                name="target-direction"
                checked={targetOperator === "lte"}
                onChange={() => onTargetOperatorChange("lte")}
              />
              Down
            </label>
          </fieldset>
          <label className={styles.magnitudeField}>
            <span>Move by at least</span>
            <span className={styles.percentageInput}>
              <input
                ref={thresholdInputRef}
                value={targetThreshold}
                onChange={(event) => onTargetThresholdChange(event.target.value)}
                inputMode="decimal"
                placeholder="5"
                aria-label="Percentage magnitude"
                aria-invalid={invalidThreshold || undefined}
                aria-describedby={invalidThreshold ? errorMessageId : undefined}
              />
              <span aria-hidden="true">%</span>
            </span>
            <small>Enter 5 for a 5% move.</small>
          </label>
        </div>
        <p className={wizardStyles.rulePreview}>
          <strong>Alert rule</strong>
          {describeWatchSignalCondition(
            presenter,
            underlying,
            targetOperator,
            targetThreshold,
          )}
        </p>
      </div>
    );
  }

  if (isStandardizedOptionChainPresenter(presenter)) {
    const presentation = {
      ATM_IV_30D: {
        label: "30-day implied volatility",
        placeholder: "45",
        note: "Enter 45 for 45% annualized implied volatility.",
        measured: "Uses a standardized 30-calendar-day at-the-money options cohort.",
      },
      IV_RV_SPREAD_30D_20D: {
        label: "Implied-minus-realized volatility",
        placeholder: "10",
        note: "Enter 10 for a 10 percentage-point spread.",
        measured: "Compares standardized 30-day implied volatility with completed-session 20-day realized volatility.",
      },
      RISK_REVERSAL_30D_25_DELTA: {
        label: "30-day put/call skew",
        placeholder: "5",
        note: "Enter 5 for 5 percentage points. Positive values mean put volatility is higher.",
        measured: "Compares standardized 25-delta put and call implied volatility at 30 calendar days.",
      },
      ATM_IV_TERM_SLOPE_60D_MINUS_30D: {
        label: "30-to-60-day IV slope",
        placeholder: "3",
        note: "Enter 3 for 3 percentage points. Positive values mean the 60-day estimate is higher.",
        measured: "Subtracts standardized 30-day at-the-money IV from standardized 60-day at-the-money IV.",
      },
      EXPECTED_MOVE_30D: {
        label: "30-day expected move",
        placeholder: "8",
        note: "Enter 8 for an 8% one-standard-deviation estimate. This is not a forecast.",
        measured: "Uses the standardized 30-day options cohort and reports a ratio of the underlying price.",
      },
      ATM_QUOTE_COVERAGE_30D: {
        label: "Options quote coverage",
        placeholder: "80",
        note: "Enter 80 for 80% valid two-sided quote coverage.",
        measured: "Measures valid two-sided quotes in a standardized near-the-money 30-day cohort.",
      },
      ATM_MEDIAN_RELATIVE_SPREAD_30D: {
        label: "Typical near-ATM spread",
        placeholder: "8",
        note: "Enter 8 for a typical spread equal to 8% of quote midpoint.",
        measured: "Uses the median midpoint-relative spread across a coverage-qualified near-the-money cohort.",
      },
    }[presenter.measurement];
    return (
      <div className={`${styles.root} ${className ?? ""}`}>
        <p className={wizardStyles.help}>Set a factual options-market boundary. Reaching the boundary counts.</p>
        <div className={wizardStyles.triggerRow}>
          <label>
            <span>{presentation.label}</span>
            <span className={wizardStyles.percentageInput}>
              <input
                ref={thresholdInputRef}
                value={canonicalRatioToPercent(targetThreshold)}
                onChange={(event) => onTargetThresholdChange(
                  percentToCanonicalRatio(event.target.value),
                )}
                inputMode="decimal"
                placeholder={presentation.placeholder}
                aria-label={presentation.label}
                aria-invalid={invalidThreshold || undefined}
                aria-describedby={invalidThreshold ? errorMessageId : undefined}
              />
              <span aria-hidden="true">%</span>
            </span>
            <small>{presentation.note}</small>
          </label>
          <fieldset
            ref={operatorGroupRef}
            aria-invalid={invalidOperator || undefined}
            aria-describedby={invalidOperator ? errorMessageId : undefined}
          >
            <legend>Alert when the measurement is</legend>
            <label><input type="radio" name="target-direction" checked={targetOperator === "gte"} onChange={() => onTargetOperatorChange("gte")} />At or above</label>
            <label><input type="radio" name="target-direction" checked={targetOperator === "lte"} onChange={() => onTargetOperatorChange("lte")} />At or below</label>
          </fieldset>
        </div>
        <details>
          <summary>How this is measured</summary>
          <p>{presentation.measured} The Watch does not evaluate when current, coverage-qualified data is unavailable.</p>
        </details>
        <p className={wizardStyles.rulePreview}>
          <strong>Alert rule</strong>
          {describeWatchSignalCondition(presenter, underlying, targetOperator, targetThreshold)}
        </p>
      </div>
    );
  }

  return (
    <div className={`${styles.root} ${className ?? ""}`}>
      <p className={wizardStyles.help}>Set the exact price boundary. Reaching the boundary counts.</p>
      {currentPrice !== null ? (
        <p className={styles.currentPrice} role="status">
          {underlying.trim().toUpperCase() || "This ticker"} is trading at ${currentPrice.toFixed(2)}.
        </p>
      ) : null}
      <div className={wizardStyles.triggerRow}>
        <label>
          <span>Target price</span>
          <input
            ref={thresholdInputRef}
            value={targetThreshold}
            onChange={(event) => onTargetThresholdChange(event.target.value)}
            inputMode="decimal"
            placeholder="250.00"
            aria-label="Target price"
            aria-invalid={invalidThreshold || undefined}
            aria-describedby={invalidThreshold ? errorMessageId : undefined}
          />
          {currentPrice !== null && onSelectPricePreset ? (
            <span className={styles.pricePresets} role="group" aria-label="Target price presets">
              {PRICE_PRESET_OFFSETS.map((offsetPercent) => {
                const presetPrice = Math.round(currentPrice * (1 + offsetPercent / 100) * 100) / 100;
                const sign = offsetPercent > 0 ? "+" : "";
                return (
                  <button
                    key={offsetPercent}
                    type="button"
                    className={styles.presetChip}
                    onClick={() => onSelectPricePreset(presetPrice)}
                    aria-label={`Set target price to ${sign}${offsetPercent}% of the current price, $${presetPrice.toFixed(2)}`}
                  >
                    {sign}{offsetPercent}%
                  </button>
                );
              })}
              {previousClose !== null ? (
                <button
                  type="button"
                  className={styles.presetChip}
                  onClick={() => onSelectPricePreset(previousClose)}
                  aria-label={`Set target price to the previous close, $${previousClose.toFixed(2)}`}
                >
                  Prev close
                </button>
              ) : null}
            </span>
          ) : null}
        </label>
        <fieldset
          ref={operatorGroupRef}
          aria-invalid={invalidOperator || undefined}
          aria-describedby={invalidOperator ? errorMessageId : undefined}
        >
          <legend>Alert when the price</legend>
          <label>
            <input
              type="radio"
              name="target-direction"
              checked={targetOperator === "gte"}
              onChange={() => onTargetOperatorChange("gte")}
            />
            Reaches or rises above
          </label>
          <label>
            <input
              type="radio"
              name="target-direction"
              checked={targetOperator === "lte"}
              onChange={() => onTargetOperatorChange("lte")}
            />
            Reaches or falls below
          </label>
          {directionInferred ? (
            <small className={styles.inferredNote} role="status">
              Chosen from the current price. Pick the other option to flip it.
            </small>
          ) : null}
        </fieldset>
      </div>
      <p className={wizardStyles.rulePreview}>
        <strong>Alert rule</strong>
        {describeWatchSignalCondition(
          presenter,
          underlying,
          targetOperator,
          targetThreshold,
        )}
      </p>
    </div>
  );
}
